import axios from 'axios';

/**
 * Akademik yil secimi.
 *
 * Uygulamada yuzlerce fetch cagrisi var; her birine tek tek baslik eklemek yerine
 * window.fetch bir kez sarmalanir ve secili yil X-Academic-Year olarak eklenir.
 * Backend bu basligi okuyup veritabani oturumuna yazar, courses/course_info view'lari
 * da ona gore filtreler. Boylece mevcut istek kodlarinin hicbiri degismez.
 *
 * Gecmis yil salt-okunurdur; bu kural veritabaninda trigger ile zorlanir.
 * Buradaki isReadOnly() yalnizca arayuzde rozet/kilit gostermek icindir.
 */

const STORAGE_KEY = 'academicYear';
const CURRENT_STORAGE_KEY = 'academicYearCurrent';
const EXPLICIT_SELECTION_KEY = 'academicYearExplicitSelection';
export const ACADEMIC_YEAR_POLL_INTERVAL_MS = 30000;

let currentYearMeta = null; // { year, label, is_current } - /api/academic-years'ten

// Arşiv görünümünde yalnızca akademik yıl yönetimi yazma engelinden muaftır.
// Dönem sonucu kesinleştirmesi dahil hiçbir ders verisi arşive yazılamaz.
function isAllowedReadOnlyWrite(url) {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const nonAcademicYearPaths = new Set([
      '/api/admin/login',
      '/api/admin/logout',
      '/api/admin/register',
      '/api/admin/reset-password',
      '/api/admin/pretest-permission',
      '/api/admin/learning-outcomes-edit-permission',
      '/api/admin/syllabus-upload-permission',
      '/api/akademisyen/login',
      '/api/akademisyen/register',
      '/api/talep',
      '/api/talep-yanit',
      '/api/talep-okundu',
      '/api/auth/reset-password-with-code',
    ]);
    if (nonAcademicYearPaths.has(pathname) || pathname.startsWith('/api/roles/')) return true;
    if (pathname === '/api/admin/academic-years/advance'
        || pathname === '/api/admin/academic-years/activate') return true;
    if (currentYearMeta?.status === 'planning') {
      return pathname === '/api/akademisyen/upload-excel'
        || pathname === '/api/akademisyen/assign-course'
        || pathname === '/api/akademisyen/remove-course'
        || pathname === '/api/akademisyen/remove-course-by-details'
        || pathname === '/api/admin/auto-assign-courses'
        || /^\/api\/program-outcomes(?:\/\d+|\/item\/\d+)?$/.test(pathname);
    }
    return false;
  } catch {
    return false;
  }
}

export function getSelectedYear() {
  // Aynı tarayıcıda daha önce bir yönetici arşiv yılı seçmiş olabilir.
  // Öğrenci oturumu bu localStorage değerini asla devralmamalı; yıl başlığı
  // gönderilmez ve backend doğrudan aktif akademik yılı kullanır.
  const role = (sessionStorage.getItem('userRole') || '').toLocaleLowerCase('tr-TR');
  if (['student', 'ogrenci', 'öğrenci'].includes(role)) return '';
  // Eski localStorage secimini yeni oturuma tasima. Varsayilan her zaman
  // API'nin bildirdigi aktif yildir; arsiv secimi yalnizca kullanici bu
  // sekmede menuden ozellikle sectiyse korunur.
  return sessionStorage.getItem(EXPLICIT_SELECTION_KEY)
    || localStorage.getItem(CURRENT_STORAGE_KEY)
    || '';
}

export function setSelectedYear(year) {
  if (year) {
    localStorage.setItem(STORAGE_KEY, year);
    sessionStorage.setItem(EXPLICIT_SELECTION_KEY, year);
  } else {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(EXPLICIT_SELECTION_KEY);
  }
  // Secili yil tum ekranlarin verisini degistirir; en guvenli yol tam yenileme.
  window.location.reload();
}

export function setYearMeta(meta) {
  currentYearMeta = meta;
  document.body.classList.toggle('academic-year-readonly', Boolean(meta && !meta.editable));
  document.body.classList.toggle('academic-year-archived', meta?.status === 'archived');
  window.dispatchEvent(new CustomEvent('academic-year-meta-changed', { detail: meta }));
}

/**
 * API'den gelen akademik yil listesini tek noktada yorumlar.
 * Kullanici arsiv yilini bilerek sectiyse secim korunur. Onceki aktif yil
 * seciliyken merkezde yil degisirse yeni aktif yila gecilir.
 */
export function reconcileAcademicYears(data) {
  if (!Array.isArray(data)) return null;

  const current = data.find((item) => item.is_current && item.status === 'active')
    || data.find((item) => item.is_current)
    || null;
  const previousCurrent = localStorage.getItem(CURRENT_STORAGE_KEY) || '';
  const explicitSelection = sessionStorage.getItem(EXPLICIT_SELECTION_KEY) || '';
  const stored = getSelectedYear();
  const currentChanged = Boolean(
    current?.year && previousCurrent && previousCurrent !== current.year
  );
  const explicitMeta = data.find((item) => item.year === explicitSelection);
  const preserveExplicitSelection = Boolean(
    explicitMeta
    && explicitSelection !== previousCurrent
  );
  const selected = preserveExplicitSelection
    ? explicitSelection
    : (current?.year || '');

  if (current?.year) localStorage.setItem(CURRENT_STORAGE_KEY, current.year);
  if (selected) localStorage.setItem(STORAGE_KEY, selected);
  else localStorage.removeItem(STORAGE_KEY);
  if (!preserveExplicitSelection) {
    sessionStorage.removeItem(EXPLICIT_SELECTION_KEY);
  }

  const selectedMeta = data.find((item) => item.year === selected) || current;
  setYearMeta(selectedMeta || null);

  return {
    years: data,
    current,
    selected,
    currentChanged,
    selectionChanged: selected !== stored,
  };
}

export function startAcademicYearMonitor(loadYears, onUpdate, intervalMs = ACADEMIC_YEAR_POLL_INTERVAL_MS) {
  let stopped = false;
  let requestInFlight = false;

  const refresh = async () => {
    if (stopped || requestInFlight) return;
    requestInFlight = true;
    try {
      const data = await loadYears();
      const result = reconcileAcademicYears(data);
      if (!stopped && result) onUpdate(result);
    } catch {
      // Gecici ag hatalarinda mevcut yil secimi korunur; sonraki tur yeniden dener.
    } finally {
      requestInFlight = false;
    }
  };

  const refreshWhenVisible = () => {
    if (document.visibilityState === 'visible') refresh();
  };

  refresh();
  const timerId = window.setInterval(refresh, intervalMs);
  document.addEventListener('visibilitychange', refreshWhenVisible);
  window.addEventListener('focus', refresh);

  return () => {
    stopped = true;
    window.clearInterval(timerId);
    document.removeEventListener('visibilitychange', refreshWhenVisible);
    window.removeEventListener('focus', refresh);
  };
}

export function announceAcademicYearChange(result) {
  if (!result?.currentChanged || !result.current) return false;
  window.alert(
    `Akademik yıl ${result.current.label || result.current.year} olarak güncellendi.`
  );
  return result.selectionChanged;
}

/** Secili yil duzenlenebilir mi (yalnizca guncel yil duzenlenebilir). */
export function isReadOnly() {
  if (!currentYearMeta) return false;
  return !currentYearMeta.editable;
}

export function isArchivedYear() {
  return currentYearMeta?.status === 'archived';
}

export function isActiveYear() {
  return currentYearMeta?.status === 'active'
    && currentYearMeta?.is_current === true
    && currentYearMeta?.editable === true;
}

/** window.fetch'i bir kez sarmala: secili yili her istege ekle. */
export function installAcademicYearHeader() {
  if (!window.__academicYearFetchPatched) {
    window.__academicYearFetchPatched = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = (input, init = {}) => {
      const year = getSelectedYear();
      const method = (init.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
      const requestUrl = input instanceof Request ? input.url : input;
      if (isReadOnly() && method !== 'GET' && method !== 'HEAD'
        && !isAllowedReadOnlyWrite(requestUrl)) {
        return Promise.reject(new Error('Arşiv akademik yılında değişiklik yapılamaz.'));
      }
      const requestInit = { credentials: 'include', ...init };
      if (!year) return originalFetch(input, requestInit);

      const headers = new Headers(
        (init && init.headers) || (input instanceof Request ? input.headers : undefined)
      );
      // İstek belirli bir hedef yılı açıkça gönderiyorsa (ör. yeni akademik yıl
      // hazırlık ekranı), genel seçici bu değeri ezmemelidir.
      if (!headers.has('X-Academic-Year')) {
        headers.set('X-Academic-Year', year);
      }

      if (input instanceof Request) {
        return originalFetch(new Request(input, { credentials: 'include', headers }));
      }
      return originalFetch(input, { ...requestInit, headers });
    };
  }

  // Uygulamanin cogu API cagrisi axios kullaniyor. Yil secimi fetch ile sinirli
  // kalirsa arsiv yilinda bazi ekranlar varsayilan (guncel) yili gorurdu.
  if (!window.__academicYearAxiosPatched) {
    window.__academicYearAxiosPatched = true;
    axios.interceptors.request.use((config) => {
      config.withCredentials = true;
      const year = getSelectedYear();
      const method = (config.method || 'get').toLowerCase();
      if (isReadOnly() && !['get', 'head', 'options'].includes(method)
        && !isAllowedReadOnlyWrite(config.url || '')) {
        return Promise.reject(new Error('Arşiv akademik yılında değişiklik yapılamaz.'));
      }
      if (year) {
        config.headers = config.headers || {};
        const hasExplicitYear = typeof config.headers.has === 'function'
          ? config.headers.has('X-Academic-Year')
          : Boolean(config.headers['X-Academic-Year'] || config.headers['x-academic-year']);
        if (!hasExplicitYear) {
          config.headers['X-Academic-Year'] = year;
        }
      }
      return config;
    });
  }
}
